import { Component, OnInit, Input, OnChanges, ViewChild } from '@angular/core';
import { DeviceService } from '../../device.service';
import { CommonService, TimeSource } from 'src/app/shared/services/common.service';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import * as _ from 'underscore';
import { UsersService } from 'src/app/users/users.service';
import { DeviceFactory } from '../../device-factory/device-factory';
import { ColumnsConfig, TableConfig, TableData } from 'sct-custom-table/sct-table/projects/sct-table/src/lib/custom-table-interface';
import { mainDeviceConfig } from '../../device-factory/device-config';
import { DeviceTypeService } from '../../device-type.service';
import { SitesService } from 'src/app/sites/sites.service';
import { SiteTemperatureUnit } from '../../../site-settings/site-utilization-settings/site-utilization-settings.interface';

interface EventTableConfig {
	columnConfig: ColumnsConfig[],
	tableData: TableData[],
	tableConfig: TableConfig,
	hasData: boolean
}

@Component({
	selector: 'app-events',
	templateUrl: './events.component.html'
})
export class EventsComponent implements OnInit, OnChanges  {

	@Input() device: any = {};
	@Input() currentSite: any;
	@Input() dateRange: any = {};
	@Input() invalidDateRange: boolean = false;
	@Input() showCorruptedEvents: boolean = false;
	@Input() showDefectiveEvents: boolean = false;
	@Input() studyId: null | number = null;
	@Input() studyInfo: any = {};

	exportFileName: string = "";
	exportDefectiveFileName = "";
	oneDayInSeconds: number = 24* 60 * 60;
	currentUser: any = {};
	events: any[] = [];
	corruptedEvents: any[] = [];
	defectiveEvents: any[] = [];
	hasGrid: boolean = false;
	isIMDeviceType = false;
	DeviceFactory = DeviceFactory;
	initialized = false;
	eventTables = {
		events: {columnDefs: [], hasGrid: false},
		offEvent: {columnDefs: [], hasGrid: false},
		defectiveEvents: {columnDefs: [], hasGrid: false}
	}

	vpcDropDownList: any = [];
	enterprisePermissions: any = {};
	allColumnsKeys: string[] = [];
	wsToKwhrRatio: any = 3600000;
	/**
	 * Which time zone each time column is rendered in. See ST-2326.
	 * All four are device-reported values drawn unshifted - see eventZoneFields above and
	 * getDaySeconds(device.zone_id, day) in DAO/dailyDetailsDAO.js.
	 */
	private readonly timeSourceByColumn: Record<string, TimeSource> = {
		timestamp: TimeSource.Device,
		end_timestamp: TimeSource.Device,
		max_temperature_recorded_timestamp: TimeSource.Device,
		insertion_time: TimeSource.Utc,
	};

	readonly eventZoneFields: string[] = [
		"max_temperature_recorded_timestamp",
		"timestamp",
		"end_timestamp",
	];

	readonly durationFields: string[] = [
		"duration",
		"accurate_inuse_seconds",
		"accurate_charge_seconds",
		"event_duration",
		"lift_time",
		"travel_time",
		"calc_duration",
		"consumed_energy_duration",
		"generated_energy_duration"
	];

	readonly floatFields: string[] = [
		'min_voltage',
		'max_voltage',
		'start_voltage',
		'event_max_current',
		'event_min_current',
		'event_average_current',
		'latitude',
		'longitude',
		'charge_kwhr',
		'inuse_kwhr',
		'charge_ahr',
		'inuse_ahr',
		'end_voltage',
		'billed_kwh',
		'max_current',
		'min_current',
		'event_generated_kvarh',
		'event_generated_energy',
		'event_consumed_energy',
		'positive_peak_power',
		'negative_peak_power',
	];

	readonly booleanFields = [
		'midnight_split',
		'time_change_split',
		'water_level_exists',
		'water_flow_exists',
		'temperature_exists',
		'system_start',
		'first_time_configuration',
		'fw_update',
		'user_restart_request',
		'set_rtc',
		'set_serial_number',
		'update_config',
		'data_from_bv',
		'split_at_start',
		'split_at_end',
		'flag_finish',
		'flag_eq',
		'flag_finish_start',
		'flag_eq_start',
		'meter_changed',
		'status_flag_safe_close',
		'modbus_failed',
		'ct_set',
		'full_power_reset',
		'zone_change',
		'hot_disconnection'
	];

	readonly integerFields = [
		'sequence_id',
		'bv_id',
		'bv_event_id',
		'bv_time_diff',
		'vpc',
		'max_temperature_recorded',
		'hall_effect_count',
		'start_soc',
		'end_soc',
		'number_of_cells',
		'bv_time_zone',
	];

	readonly dateTimeFields = [
		"max_temperature_recorded_timestamp",
		"timestamp",
		"end_timestamp",
		"insertion_time",
	];

	readonly helpFields = [
		'sequence_id',
		'timestamp',
		'end_timestamp',
		'event_type',
		'event_duration',
		'inuse_ahr',
		'charge_kwhr',
		'inuse_kwhr',
		'charge_ahr',
		'min_voltage',
		'max_voltage',
		'start_voltage',
		'end_voltage',
		'event_max_current',
		'event_average_current',
		'start_soc',
		'end_soc',
		'number_of_cells',
		'vpc',
		'accurate_inuse_seconds',
	];

	readonly voltageFields = [
		'max_voltage',
		'min_voltage'
	];

	readonly energyFields = [
		'inuse_kwhr',
		'charge_kwhr',
		'accurate_inuse_seconds'
	];

	readonly absFields = [
		'max_voltage',
		'min_voltage',
		'max_current',
		'min_current',
		'max_voltage_ll',
		'min_voltage_ll'
	];

	readonly noReadingFlagByField: Record<string, string> = {
		min_voltage: 'min_voltage',
		max_voltage: 'min_voltage',
		min_current: 'min_current',
		max_current: 'min_current',
		event_min_current: 'event_min_current',
		event_max_current: 'event_min_current',
		min_voltage_ll: 'min_voltage_ll',
		max_voltage_ll: 'min_voltage_ll',
	};

	readonly noReadingFields = Object.keys(this.noReadingFlagByField);

	readonly stringFields = ['latitude', 'longitude', 'event_type'];

	readonly hiddenFields = [
		'midnight_split',
		'time_change_split',
		'first_time_configuration',
		'fw_update',
		'user_restart_request',
		'set_serial_number',
	];

	readonly temperatureFields = [
		'max_temperature_recorded',
		'max_temperature_recorded_timestamp',
		'temperature_exists'
	];

	temperatureUnit: number = SiteTemperatureUnit.FAHRENHEIT;

	readonly waterFields = [
		'water_level_exists',
		'water_level_high',
		'water_flow_exists',
		'hall_effect_count',
	];

	readonly waterFieldsEnable = [
		'water_level_exists',
		'water_level_high'
	];

	readonly eqFields = [
		'flag_eq',
		'flag_eq_start',
		'flag_eq_end',
		'flag_finish',
		'flag_finish_start',
		'finish_reason',
		'eq_reason'
	];

	readonly BATTERY_VOLTAGES_FIELDS: string[] = ['start_voltage', 'end_voltage', 'min_voltage', 'max_voltage'];

	readonly VOLTAGE_DISPLAY = {
		BATTERY_VOLTAGE: 'battery',
		VPC: 'vpc',
	};

	private durationFieldsSet = new Set(this.durationFields);
	private booleanFieldsSet = new Set(this.booleanFields);
	private floatFieldsSet = new Set(this.floatFields);
	private integerFieldsSet = new Set(this.integerFields);
	private dateTimeFieldsSet = new Set(this.dateTimeFields);
	private absFieldsSet = new Set(this.absFields);
	private noReadingFieldsSet = new Set(this.noReadingFields);
	private eventZoneFieldsSet = new Set(this.eventZoneFields);
	private batteryVoltagesFieldsSet = new Set(this.BATTERY_VOLTAGES_FIELDS);
	private stringFieldsSet = new Set(this.stringFields);
	private eqFieldsSet = new Set(this.eqFields);
	private waterFieldsSet = new Set(this.waterFields);
	private waterFieldsEnableSet = new Set(this.waterFieldsEnable);
	private temperatureFieldsSet = new Set(this.temperatureFields);
	private hiddenFieldsSet = new Set(this.hiddenFields);
	private helpFieldsSet = new Set(this.helpFields);

	voltageDisplayMode = {
		event: this.VOLTAGE_DISPLAY.VPC,
		offEvent: this.VOLTAGE_DISPLAY.VPC,
		defectiveEvent: this.VOLTAGE_DISPLAY.VPC,
	};

	eventsDefaultFields = [];

	readonly offEventLimit: number = 60;
	isAdminUser: Boolean = false;

	// table variables
	commonTableConfig: TableConfig = {
		hasExport: true,
		hasReset: true,
		hasPagination: true,
		pageSize: 100,
		fitScreen: true,
		draggable: true,
		fileName: '',
		tableId: '',
		csvFileHeaderInfo: []
	};

	tablesConfig: {event: EventTableConfig, offEvent: EventTableConfig, defectiveEvent: EventTableConfig} = {
		event:  {
			columnConfig: [],
			tableData: [],
			tableConfig: {...this.commonTableConfig},
			hasData: false
		},
		offEvent: {
			columnConfig: [],
			tableData: [],
			tableConfig: {...this.commonTableConfig},
			hasData: false
		},
		defectiveEvent: {
			columnConfig: [],
			tableData: [],
			tableConfig: {...this.commonTableConfig},
			hasData: false
		}
	}

	updateTableData(type: string) {
		switch (type) {
			case 'event':
				this.tablesConfig.event.tableData = this.fillEventTablesData(this.events, false, this.voltageDisplayMode.event);
				break;
			case 'offEvent':
				this.tablesConfig.offEvent.tableData = this.fillEventTablesData(this.corruptedEvents, true, this.voltageDisplayMode.offEvent);
				break;
			case 'defectiveEvent':
				this.tablesConfig.defectiveEvent.tableData = this.fillEventTablesData(this.defectiveEvents, false, this.voltageDisplayMode.defectiveEvent);
				break;
			default:
				break;
		}
	}

	// ST-1502
	// readonly frozenColumns: string[] = ['sequence_id', 'timestamp', 'end_timestamp', 'event_type'];

	deviceConfig: mainDeviceConfig | null;

	constructor(
		private deviceService: DeviceService,
		private translateService: TranslateService,
		private commonService: CommonService,
		private usersService: UsersService,
		private deviceTypeService: DeviceTypeService,
		private siteService: SitesService,
	) { }

	ngOnInit() {
		this.initialized = true;
		this.isIMDeviceType = DeviceFactory.isIMDeviceType(this.device.config_info.sct_device_type);
		this.deviceConfig = DeviceFactory.createDevice(this.device.config_info.sct_device_type).config;
		this.setDeviceFields();

		this.siteService.getSiteUtilizationSettings(this.device.config_info.site_id).subscribe((res: any) => {
			const siteUtilizationSettings = res?.[0];
			if (!siteUtilizationSettings)
				return;

			const batteriesSettings = siteUtilizationSettings.batteries_utilization_settings;
			this.temperatureUnit = batteriesSettings.temperature_unit ?? SiteTemperatureUnit.FAHRENHEIT;
			this.setDeviceFields();
			if (this.events?.length)
				this.updateTableData('event');
			if (this.corruptedEvents?.length)
				this.updateTableData('offEvent');
			if (this.defectiveEvents?.length)
				this.updateTableData('defectiveEvent');
		});

		if (this.studyInfo && this.studyInfo.id)
			this.commonService.setPageTabTitle(`${this.translateService.instant('studies.power_studies')} - ${this.studyInfo.name} - ${this.translateService.instant('nav.events_log')}`);
		else
			this.commonService.setPageTabTitle(`${this.currentSite.customer_name} - ${this.currentSite.name} - ${this.device.serial_number} - ${this.translateService.instant('nav.events_log')}`);
		this.usersService.getPermissions(this.currentSite.id).subscribe((res: any) => {
			this.enterprisePermissions = res;
			this.hideTempAndWaterLevel(res);
			this.getEvents();
		});

		this.currentUser = this.usersService.getCurrentUser();
		this.isAdminUser = this.currentUser.is_admin;

		const csvFileHeaderInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.currentSite.customer_name,
			siteName: this.currentSite.name,
			deviceSN: this.device.serial_number,
			includeSpacing: false
		});

		if(DeviceFactory.isChargerDeviceType(this.device.config_info.sct_device_type))
			this.voltageDisplayMode = {
				event: this.VOLTAGE_DISPLAY.BATTERY_VOLTAGE,
				offEvent: this.VOLTAGE_DISPLAY.BATTERY_VOLTAGE,
				defectiveEvent: this.VOLTAGE_DISPLAY.BATTERY_VOLTAGE,
			};

		this.tablesConfig.event.tableConfig.csvFileHeaderInfo = csvFileHeaderInfo;
		this.tablesConfig.offEvent.tableConfig.csvFileHeaderInfo = csvFileHeaderInfo;
		this.tablesConfig.defectiveEvent.tableConfig.csvFileHeaderInfo = csvFileHeaderInfo;
		this.tablesConfig.event.tableConfig.multiSelect = true;
		this.tablesConfig.event.tableConfig.pageSizeOptions = [100, 250, 500, 1000, 2000];
		this.tablesConfig.event.tableConfig.pageSize = 200;
	}

	setDeviceFields() {
		this.allColumnsKeys = this.deviceService.getEventsFields({fwVersion: this.device.config_info.fw_version});
		this.allColumnsKeys = this.filterFields(this.allColumnsKeys);
		this.eventsDefaultFields = this.deviceService.eventsDefaultFields;

		if (this.isIMDeviceType) {
			this.allColumnsKeys = this.deviceService.imEventsDefaultFields;
			this.eventsDefaultFields = this.deviceService.imEventsDefaultFields;
		}

		if (!this.deviceConfig.events.hasFields.hasEnergyColumns) {
			this.eventsDefaultFields = this.eventsDefaultFields.filter(
				field => !this.energyFields.includes(field)
			);
		}
	}

	filterFields(columns: string[]) {
		if(!this.deviceConfig) return columns;
		
		if (this.deviceConfig.fields.hasTemperatureSensor == false) {
			columns = columns.filter(col => !this.temperatureFieldsSet.has(col));
		}
		if (this.deviceConfig.fields.hasWaterLevel == false) {
			columns = columns.filter(col => !this.waterFieldsSet.has(col));
		}
		if (this.deviceConfig.fields.hasEq == false) {
			columns = columns.filter(col => !this.eqFieldsSet.has(col));
		}
		return columns;
	}

	getEvents() {
		let fromDateRange = this.dateRange.fromDate;
		let toDateRange = this.dateRange.toDate;
		if (!this.device || !this.device.mac_address || !fromDateRange || !toDateRange) return;

		fromDateRange = new Date(new Date(fromDateRange).setHours(0, 0, 0, 0));
		toDateRange = new Date(new Date(toDateRange).setHours(23, 59, 59, 999));

		const formattedFromDateRange = moment(fromDateRange).format('YYYY.MM.DD');
		const formattedToDateRange = moment(toDateRange).format('YYYY.MM.DD');
		this.exportFileName = `${this.device.serial_number}-${formattedFromDateRange}-${formattedToDateRange}.csv`;
		this.exportDefectiveFileName = `${this.device.serial_number}-${formattedFromDateRange}-${formattedToDateRange}-defective-events.csv`;

		this.updateTablesConfigs();
		let zoneDiff = new Date().getTimezoneOffset() * -1;
		let fromDate: any = new Date(new Date(fromDateRange).getTime() + (zoneDiff * 60 * 1000));
		let toDate: any = new Date(new Date(toDateRange).getTime() + (zoneDiff * 60 * 1000));
		fromDate = moment(fromDate).utc().startOf('day').unix();
		toDate = moment(toDate).utc().endOf('day').unix();

		this.deviceService.getEvents(this.device.mac_address, fromDate, toDate, this.studyId).subscribe((data: any) => {
			this.events = this.formatData(data.events);
			this.corruptedEvents = this.formatData(data.corrupted, true);
			this.defectiveEvents = this.formatData(data.defective, false, true);

			this.setTablesColumnsConfigs();
			this.updateTablesConfigs();

			this.tablesConfig.event.tableData = this.fillEventTablesData(this.events, false, this.voltageDisplayMode.event);
			if (this.showCorruptedEvents)
				this.tablesConfig.offEvent.tableData = this.fillEventTablesData(this.corruptedEvents, true, this.voltageDisplayMode.offEvent);

			if (this.showDefectiveEvents)
				this.tablesConfig.defectiveEvent.tableData = this.fillEventTablesData(this.defectiveEvents, false, this.voltageDisplayMode.defectiveEvent);
		});
	}

	updateTablesConfigs() {
		this.tablesConfig.event.tableConfig.fileName = this.exportFileName.replace('.csv', '');
		this.tablesConfig.event.tableConfig.tableId = 'events';

		this.tablesConfig.offEvent.tableConfig.fileName = this.exportFileName.replace('.csv', '') +  '-off-events';
		this.tablesConfig.offEvent.tableConfig.tableId = 'off-events';

		this.tablesConfig.defectiveEvent.tableConfig.fileName = this.exportDefectiveFileName.replace('.csv', '');
		this.tablesConfig.defectiveEvent.tableConfig.tableId = 'defective-events';

		// ST-1502
		// if (this.usersService.userHasNOCAccess())
		// 	this.tablesConfig.event.columnConfig = this.applyFrozenColumns(this.tablesConfig.event.columnConfig);
	}

	// Hide events (idle, charge, Inuse and off) if user type not NOC or Admin and duration less than 1 Mints
	hideEventsType(events: any) {
		if (!(this.usersService.userHasNOCAccess() || this.currentUser.is_admin)) {
			const newEvents = events.filter((item: any) => item.calc_duration >= 60 || item.event_duration >= 60);
			return newEvents;
		}
		return events;
	}

	hideTempAndWaterLevel(enterprisePermissions: any) {
		if(!this.usersService.userHasNOCAccess(enterprisePermissions)) {
			if (!this.isWaterLevelEnabled()) 
				this.allColumnsKeys = this.allColumnsKeys.filter(key => !this.waterFieldsEnable.includes(key));

			if (!this.isTemperatureEnabled()) 
				this.allColumnsKeys = this.allColumnsKeys.filter(key => !this.temperatureFields.includes(key));	
		}
	}

	getEventTypesOptions() {
		let eventTypes =  [
			{ value: this.translateService.instant('events.event_type_1'), label: this.translateService.instant('events.event_type_1') },
			{ value: this.translateService.instant('events.event_type_2'), label: this.translateService.instant('events.event_type_2') },
			{ value: this.translateService.instant('events.event_type_3'), label: this.translateService.instant('events.event_type_3') },
		];

		if (![DeviceFactory.types.battlink, DeviceFactory.types.battlinkII, DeviceFactory.types.lithiumBattlink].includes(this.device.config_info.sct_device_type) || this.usersService.userHasNOCAccess()) {
			eventTypes.push({ value: this.translateService.instant('events.event_type_off'), label: this.translateService.instant('events.event_type_off') });
		}

		if (this.isIMDeviceType) {
			eventTypes =  [
				{ value: this.translateService.instant('events.im_event_type_0'), label: this.translateService.instant('events.im_event_type_0') },
				{ value: this.translateService.instant('events.im_event_type_1'), label: this.translateService.instant('events.im_event_type_1') },
				{ value: this.translateService.instant('events.im_event_type_2'), label: this.translateService.instant('events.im_event_type_2') },
				{ value: this.translateService.instant('events.im_event_type_3'), label: this.translateService.instant('events.im_event_type_3') },
			];
		}

		return eventTypes;
	}

	setTablesColumnsConfigs() {
		let shownFields = this.eventsDefaultFields;
		let adminFields = this.deviceService.eventsAdminFields;
		let NocAccessFields = this.deviceService.eventsNocAccessFields;
		let debugDataFields = this.deviceService.eventsDebugDataFields;

		const existsColumnsMap = {
			event:          this.getExistColumns(this.events),
			offEvent:       this.getExistColumns(this.corruptedEvents),
			defectiveEvent: this.getExistColumns(this.defectiveEvents),
		};

		const eventTables = [
			{ key: 'event', data: this.events, existsColumns: existsColumnsMap.event },
			{ key: 'offEvent', data: this.corruptedEvents, existsColumns: existsColumnsMap.offEvent },
			{ key: 'defectiveEvent', data: this.defectiveEvents, existsColumns: existsColumnsMap.defectiveEvent },
		];

		const deviceConfig = DeviceFactory.createDevice(this.device.config_info.sct_device_type).config;
		const toSkipFields = Object.keys(deviceConfig.events.hasFields);
		const hasOrPowerStudyFields = Object.keys(deviceConfig.events.hasOrPowerStudy);
		const userHasNOCAccess = this.usersService.userHasNOCAccess(this.enterprisePermissions);
		const userHasDebugDataAccess = this.usersService.hasAccessPermission(this.enterprisePermissions, 'debug_data', 'read', null, deviceConfig.permissionMapperKey);

		for (let table of eventTables) {
			if (!table.data.length) {
				this.tablesConfig[table.key].hasData = false;
				continue;
			}

			let gridColumns = [];

			for (let field of this.allColumnsKeys) {
				if (toSkipFields.includes(field) && deviceConfig.events.hasFields[field] === false)
					continue;

				if (hasOrPowerStudyFields.includes(field) && !(this.deviceTypeService.isStudyDevice(this.device.config_info) || deviceConfig.events.hasOrPowerStudy[field] === true)) 
					continue;

				let fieldLabel = this.translateService.instant('events.'+field);
				
				if (field === 'water_level_high') {
					fieldLabel = this.translateService.instant('events.water_level');
				}

				if (this.voltageFields.includes(field)) {
					let suffix = '';
					if (this.device?.config_info?.voltage_reference === this.deviceService.lineToLine && this.voltageFields.includes(field))
						suffix += '_ll';

					fieldLabel = this.deviceService.translate('events.'+field+suffix, this.device.config_info);
				}

				if (field === 'max_temperature_recorded')
					fieldLabel = `${fieldLabel} (${this.commonService.tempUnitSymbol(this.temperatureUnit)})`;

				let columnConfig: ColumnsConfig = {key: field, name: fieldLabel, type: 'string'};

				if (table.existsColumns.includes(field)) {
					if (field === 'water_level_high') {
						columnConfig.type = 'string';
						columnConfig.filterType = 'dropdown';
						columnConfig.items = [
							{ label: 'Low', value: 'Low' },
							{ label: 'High', value: 'High' }
						];
					} 
					else if (this.booleanFieldsSet.has(field)) {
						columnConfig.type = 'boolean';
						columnConfig.noValueString = '-';
						columnConfig.exportEmptyString = null;
					} else if (this.integerFieldsSet.has(field)) {
						columnConfig.type = 'number';
						columnConfig.noValueString = 'N/A';
					} else if (this.floatFieldsSet.has(field)) {
						columnConfig.type = 'number';
						columnConfig.noValueString = 'N/A';
					} else if (this.dateTimeFieldsSet.has(field)) {
						columnConfig.type = 'date';
					} else if (this.durationFieldsSet.has(field)) {
						columnConfig.type = 'duration';
						columnConfig.key = field+"_formatted";
					} else if (field == 'event_type') {
						columnConfig.items = this.getEventTypesOptions();
						columnConfig.filterType = 'dropdown';
					} else if (field == 'battery_type') {
						columnConfig.items = [
							{ label: '', value: null },
							{ value: this.translateService.instant('events.battery_type_0'), label: this.translateService.instant('events.battery_type_0') },
							{ value: this.translateService.instant('events.battery_type_2'), label: this.translateService.instant('events.battery_type_2') },
							{ value: this.translateService.instant('events.battery_type_3'), label: this.translateService.instant('events.battery_type_3') },
						];

						columnConfig.filterType = 'dropdown';
					}

					if ((!this.isIMDeviceType && !shownFields.includes(field)) || (this.isIMDeviceType && this.hiddenFieldsSet.has(field)))
						columnConfig.hidden = true;

					if (adminFields.includes(field) && !this.isAdminUser)
						continue;

					if (NocAccessFields.includes(field) && !userHasNOCAccess)
						continue;

					if (debugDataFields.includes(field) && !userHasDebugDataAccess)
						continue;

					if (this.shouldAddTooltip(field))
  						columnConfig.hasTooltip = this.translateService.instant(`grid_help.events.${field}`);

					if (field == 'vpc') {
						columnConfig.items =  this.vpcDropDownList;
						columnConfig.filterType = 'dropdown';
					}

					if (this.durationFieldsSet.has(field)) {
						columnConfig.type = 'duration';
						if (table.key == 'events' || table.key == 'defectiveEvents')
							columnConfig.key = field+"_formatted";
						else if (table.key == 'offEvent')
							columnConfig.key = field+"_formatted_off";
					}
					else {
						if (table.key == 'offEvent')
							columnConfig.key = field+"_off";
					}

					gridColumns.push(columnConfig);
					this.tablesConfig[table.key].columnConfig.push(columnConfig);
				}
			}

			this.tablesConfig[table.key].hasData = true;
			this.commonService.applyTimeSourceTooltips(gridColumns, this.timeSourceByColumn);
			this.tablesConfig[table.key].columnConfig = gridColumns;
		}
	}

	getExistColumns(source: any[]) {
		if (!source.length)
			return [];

		const cols = {};

		for (const event of source) {
			if (event.sequence_id == 'N/A')
				continue;

			for (const key in event) {
				if (!cols[key]) {
					cols[key] = true;
				}
			}
		}

		const columnsList = Object.keys(cols);
		columnsList.push(
			'event_duration',
			'charge_kwhr',
			'inuse_kwhr',
			'charge_ahr',
			'inuse_ahr',
			'billed_kwh',
			'lift_time',
			'travel_time'
		);

		return columnsList;
	}

	formatData(data, getCorrupted = false, defective = false) {
		const deviceConfig = DeviceFactory.createDevice(this.device.config_info.sct_device_type).config;
		let events = [];

		let allVpcs = {};
		let vpcDropDownOptions = [
			{ label: '', value: null },
		];

		let previousEvent;
		let beforeLastEvent;

		const createOffEvents = (startTime, endTime) => {
			while (startTime < endTime) {
				const timeDiff = endTime - startTime;
				if (timeDiff < this.oneDayInSeconds) {
					let offEvent = this.getOffEvent(startTime, endTime);
					this.formatEvent(offEvent);
					events.push(offEvent);
					break;
				} else {
					const endOfDay = moment(startTime*1000).utc().endOf('day').unix();
					let offEvent = this.getOffEvent(startTime, endOfDay + 1);
					this.formatEvent(offEvent);
					events.push(offEvent);
					startTime = endOfDay + 1;
				}
			}
		}

		data.forEach((element) => {
			let event = getCorrupted ? element.event : this.commonService.decompress(element, 'events', this.device.config_info.sct_device_type);
			event.event_duration = event.end_timestamp - event.timestamp;

			if(deviceConfig.dailyDetails.useBilledKwh && ![this.commonService.usaStates.oregon, this.commonService.usaStates.california].includes(this.currentSite.state_id))
				event.charge_kwhr = event.billed_kwh;

			if (!getCorrupted && !defective && !this.isIMDeviceType) {
				let hasOffEvent = this.checkOffEvent(previousEvent, event);
				if (hasOffEvent) {
					createOffEvents(previousEvent.end_timestamp, event.timestamp);
				}
			}

			beforeLastEvent = Object.assign({}, previousEvent);
			previousEvent = Object.assign({}, event);
			this.formatEvent(event);

			if (getCorrupted)
				event.insertion_time = moment((new Date(element.insertion_time)).getTime()).utc().format('MM/DD/YYYY HH:mm:ss');

			events.push(event);

			if (!getCorrupted && !defective)
				allVpcs[event.vpc] = true;
		});

		if (!getCorrupted && !defective) {
			for (let vpc of Object.keys(allVpcs)) {
				vpcDropDownOptions.push({ label: '' + vpc, value: vpc },);
			}
			this.vpcDropDownList = vpcDropDownOptions;
		}
		return this.hideEventsType(events);
	}

	fillEventTablesData(data: any[], isOff = false, voltageDisplayMode = 'battery'): TableData[] {
		if (!data || !data.length)
			return [];

		const tableData: TableData[] = [];
		const offString = isOff ? '_off' : '';
		const numberOfCellsDevice = this.device?.config_info?.number_of_cells || 1;

		for (let event of data) {
			const tableEventData = {};
			const numberOfCells = (event.number_of_cells && event.number_of_cells !== 'N/A') ? event.number_of_cells : numberOfCellsDevice;

			for (let field of this.allColumnsKeys) {
				const isEventOff = isOff || event.event_type === 'off' || event.event_type === this.translateService.instant('events.event_type_off');

				const temperatureDisabled = this.temperatureFieldsSet.has(field) && !this.isTemperatureEnabled();
				const waterDisabled = this.waterFieldsEnableSet.has(field) && !this.isWaterLevelEnabled();
				if (temperatureDisabled || waterDisabled) {
					tableEventData[field + offString] = {
						value: '-',
						sortValue: null
					};
					continue;
				}
				else if (this.durationFieldsSet.has(field)) {
					tableEventData[field + "_formatted" + offString] = { value: event[field + "_formatted"] || event[field], sortValue: event[field] || 0 };
				} else {
					let value = event[field];
					let displayValue = value;
					let sortValue = value;

					if (isEventOff && this.booleanFields.includes(field)) {
						displayValue = 'N/A';
					}

					if (this.batteryVoltagesFieldsSet.has(field)) {
						const voltageValue = parseFloat(value);

						if (!isNaN(voltageValue)) {
							const finalValue = voltageDisplayMode === this.VOLTAGE_DISPLAY.VPC && numberOfCells ? (voltageValue / numberOfCells) : voltageValue;
							sortValue = +finalValue.toFixed(2);
							displayValue = finalValue.toFixed(2);
						}
					}

					if (field === 'max_temperature_recorded' && value !== undefined && value !== null && value !== 'N/A') {
						const convertedTemp = this.commonService.formatSensorTemp(Number(value), this.temperatureUnit);
						displayValue = convertedTemp;
						sortValue = convertedTemp;
					}

					const isNumericField = this.integerFieldsSet.has(field) || this.floatFieldsSet.has(field);

					if (this.isIMDeviceType && displayValue === this.commonService.NO_READING
						&& this.noReadingFieldsSet.has(field)) {
						tableEventData[field + offString] = { value: this.commonService.NO_READING, sortValue: null };
					}
					else if (displayValue === 'N/A' || displayValue === undefined || displayValue === null) {
						if (this.booleanFields.includes(field))
							tableEventData[field + offString] = { value: null, trueText: '-', falseText: '-', noValueString: '-', filterFalseText: '-' };
						else if (isNumericField)
							tableEventData[field + offString] = { value: '-', trueText: '-', falseText: '-', sortValue: null };
						else
							tableEventData[field + offString] = { value: '-', trueText: '-', falseText: '-' };
					} else {
						if (isNumericField && typeof sortValue !== 'number') {
							const numericSortValue = sortValue === '' ? NaN : Number(sortValue);
							sortValue = Number.isFinite(numericSortValue) ? numericSortValue : null;
						}

						tableEventData[field + offString] = { value: displayValue, sortValue };
					}
				}
			}
			tableData.push(tableEventData);
		}

		return tableData;
	}

	ngOnChanges(changes) {
		this.isIMDeviceType = DeviceFactory.isIMDeviceType(this.device.config_info.sct_device_type);

		if (!this.initialized) 
			return;

		if (changes.showCorruptedEvents?.currentValue === true && !this.tablesConfig.offEvent.tableData.length && this.corruptedEvents.length)
			this.tablesConfig.offEvent.tableData = this.fillEventTablesData(this.corruptedEvents, true, this.voltageDisplayMode.offEvent);

		if (changes.showDefectiveEvents?.currentValue === true && !this.tablesConfig.defectiveEvent.tableData.length && this.defectiveEvents.length) 
			this.tablesConfig.defectiveEvent.tableData = this.fillEventTablesData(this.defectiveEvents, false, this.voltageDisplayMode.defectiveEvent);

		if ((changes.showCorruptedEvents === undefined || changes.showCorruptedEvents.firstChange) && (changes.showDefectiveEvents === undefined || changes.showDefectiveEvents.firstChange)) {
			this.setDeviceFields(); 
			this.hideTempAndWaterLevel(this.enterprisePermissions);
			this.getEvents();
		}
	}

	checkOffEvent(previous, current) {
		if (!previous)
			return false;

		if ([DeviceFactory.types.battlink, DeviceFactory.types.battlinkII, DeviceFactory.types.lithiumBattlink].includes(this.device.config_info.sct_device_type) && !this.usersService.userHasNOCAccess())
			return false;

		// if the current event START TIME -  previous event END TIME  > 1 minute
		// neither of these events have SET RTC Flag
		if (
			current.timestamp - previous.end_timestamp > this.offEventLimit &&
			!current.set_rtc && !previous.set_rtc
		)
			return true;
	}

	getOffEvent(startTime, endTime) {
		// type OFF
		// all fields will be N/A except for start time (EVENT_A END TIME) , end time (EVENT_B start time) and duration
		let tempEvent: any = {};
		tempEvent.event_type = 'off';
		tempEvent.timestamp = startTime;
		tempEvent.end_timestamp = endTime;
		tempEvent.event_duration = endTime - startTime;
		tempEvent.battery_type = 4;
		for (let key of this.allColumnsKeys) {
			if (tempEvent[key])
				continue;

			tempEvent[key] = 'N/A';
		}
		return tempEvent;
	}

	getNoReadingFields(event): Set<string> {
		const noReading = new Set<string>();

		if (!this.isIMDeviceType)
			return noReading;

		for (const field of this.noReadingFields)
			if (this.commonService.hasNoReading(event[this.noReadingFlagByField[field]]))
				noReading.add(field);

		return noReading;
	}

	formatEvent(event) {
		let eventKeys = Object.keys(event);
		const noReading = this.getNoReadingFields(event);
		for (const field of eventKeys) {
			switch (true) {
				case noReading.has(field):
					event[field] = this.commonService.NO_READING;
					break;
				case this.eventZoneFieldsSet.has(field):
					const timestamp = Number(event[field]);
					event[field] = !isNaN(timestamp) ? moment(timestamp * 1000).utc().format('MM/DD/YYYY HH:mm:ss') : null;
					break;
				case this.durationFieldsSet.has(field):
					if(!event[field])
						event[field] = 0;

					event[field + "_formatted"] = this.commonService.timeFormat(event[field], { limitToOneDay: true });
					break;
				case this.floatFieldsSet.has(field):
					event[field] = +(event[field] / 1.0).toFixed(2);
					if (Number.isNaN(event[field]))
						event[field] = 'N/A';
					break;
				case this.booleanFieldsSet.has(field):
					event[field] = event[field] != 'N/A' ? !!event[field] : event[field];
					break;
				case !this.isIMDeviceType && this.commonService.hasNoReading(event[field]):
					event[field] = 0;
					break;
				case field === 'water_level_high':
					if (event[field] === 'N/A') 
						break;
					event[field] = event[field] ? 'High' : 'Low';
					break;
				case this.absFieldsSet.has(field):
					event[field] = Math.abs(event[field]);
					break;
				default:
					break;
			}

			if (this.isIMDeviceType) {
				if (typeof event[field] == 'number' && !this.dateTimeFieldsSet.has(field) && field != 'my_zone_id' && !this.stringFieldsSet.has(field))
					event[field] = event[field].toFixed(2);
			}
		}
		
		if (this.device?.config_info?.voltage_reference === this.deviceService.lineToLine) {
			for (const key of this.voltageFields)
				event[key] = event[key + '_ll'];
		}

		if (this.isIMDeviceType)
			event.event_type = this.translateService.instant('events.im_event_type_' + (event.event_type || 0)); // default Daily
		else
			event.event_type = this.translateService.instant('events.event_type_' + event.event_type);

		if (eventKeys.includes('charge_kwhr') && Number.isNaN(event.charge_kwhr))
			event.charge_kwhr = 'N/A';

		if (eventKeys.includes('inuse_kwhr') && Number.isNaN(event.inuse_kwhr))
			event.inuse_kwhr = 'N/A';

		if (eventKeys.includes('charge_ahr') && Number.isNaN(event.charge_ahr))
			event.charge_ahr = 'N/A';

		if (eventKeys.includes('inuse_ahr') && Number.isNaN(event.inuse_ahr))
			event.inuse_ahr = 'N/A';

		if (eventKeys.includes('my_zone_id'))
			event.my_zone_id = this.commonService.getZoneName(event.my_zone_id);

		if (eventKeys.includes('battery_type'))
			event.battery_type = this.translateService.instant('events.battery_type_' + event.battery_type);

		if (eventKeys.includes('finish_reason') && event.finish_reason != 'N/A')
			event.finish_reason = event.flag_finish ? this.translateService.instant('events.' + this.deviceService.finishReasons[event.finish_reason]) : '---';

		if (eventKeys.includes('eq_reason') && event.eq_reason != 'N/A')
			event.eq_reason = event.flag_eq && event.eq_reason ? this.translateService.instant('events.' + this.deviceService.eqReasons[event.eq_reason]) : '---';

		const reasonKey = (this.deviceService.restartReasons[event.restart_reason_l2] || {})[event.restart_reason];
		event.restart_reason = this.translateService.instant('events.restart_reason_' + (reasonKey || 'unknown'));
	}

	shouldAddTooltip(field: string): boolean {
		if (this.isIMDeviceType)
			return false;

		if (!this.helpFieldsSet.has(field))
			return false;

		if (field == 'max_voltage')
			return !this.commonService.isFWSupportField(field, this.device.config_info.fw_version);

		return true;	
	}

	onVoltageModeChange(mode: string) {
		this.voltageDisplayMode.event = mode;
		this.voltageDisplayMode.offEvent = mode;
		this.voltageDisplayMode.defectiveEvent = mode;
		this.updateTableData('event');
		this.updateTableData('offEvent');
		this.updateTableData('defectiveEvent');
	}
	isTemperatureEnabled(): boolean {
		return !!this.device?.config_info?.has_temperature_sensor;
	}

	isWaterLevelEnabled(): boolean {
		return !!this.device?.config_info?.has_water_level;
	}
	// ST-1502
	// applyFrozenColumns(columns: ColumnsConfig[]): ColumnsConfig[] {
	// 	return columns.map(col => ({
	// 		...col,
	// 		freeze: this.frozenColumns.includes(col.key) || col.freeze
	// 	}));
	// }

}
